import {JSX} from "react";
import {useControlId} from "../helpers/useControlId";
import OptionList from "../HtmlControls/OptionList";
import {MvvmControlProps} from "./MvvmControlProps";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {MvvmOptionEntry, useOptionItemMap} from "./useItemMap";
import {useMvvmSingleSelection} from "./useMvvmSelection";
import {useRegisterValidation} from "./useRegisterValidation";

export interface MvvmOptionListEntry<TValue> extends MvvmOptionEntry<TValue> {
}

export interface MvvmOptionListRenderProps<TValue> {
	name: string,
	values: string[],
	items: MvvmOptionListEntry<TValue>[];
	onChange?: (values: number[]) => void;
	onBlur?: () => void;
}

export interface MvvmOptionListProps<T extends object = never, TValue = never> extends Omit<MvvmControlProps<T, TValue>, "label"> {
	items: MvvmOptionListEntry<TValue>[];
	render?: (props: MvvmOptionListRenderProps<TValue>) => JSX.Element;
}

export default function MvvmOptionList<T extends object, TValue>(props: MvvmOptionListProps<T, TValue>): JSX.Element {
	const {
		context,
		property,
		id,
		items,
		column,
		hidden,
		disabled,
		validation,
		onChange,
		onBlur,
		render
	} = props;

	const controlId = useControlId(id);
	const itemMap = useOptionItemMap(items);
	const {selectedValues, onChangeInternal} = useMvvmSingleSelection(context, property, items, onChange);

	useRegisterValidation(context, property, controlId, hidden, validation);
	useMvvmFieldSubscription(context, property);

	if (hidden) {
		return <></>;
	}

	return render
		? render({
			name: controlId,
			items,
			values: selectedValues,
			onChange: onChangeInternal,
			onBlur
		})
		: <OptionList name={controlId} items={itemMap} values={selectedValues} disabled={disabled} column={column} onChange={onChangeInternal} onBlur={onBlur}/>;
}
